/**
 * @fileoverview Log practice (C6, decided Nov 1, 2026): the one dialog a
 * student logs a session in, from the sidebar's yellow button, the top bar's
 * plus, a "Practice this week" item on Home or the Rehearsals page's practice
 * log. The web twin of mobile/src/components/LogPracticeSheet.js.
 *
 * It asks what the session was for (the latest lesson's practice items, or
 * open assignments where those are on, or something else), how long, which
 * day (on the student's own calendar, never in the future) and an optional
 * note. Opened from an item it starts on that item. Where recordings are on it
 * offers recording instead. A log that fails stays in the dialog.
 */

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StudentAuthContext } from "@/App";
import studentApi, { createPracticeLog } from "@/lib/studentApi";
import { ASSIGNMENTS_ENABLED, rehearsalsEnabled } from "@/lib/featureFlags";
import { DURATIONS, MAX_MINUTES, latestPracticeLesson, localDay, practiceItems } from "@/lib/studentPractice";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button, Icon } from "@/components/studio/primitives";

const MAX_NOTES = 500;
const DEFAULT_MINUTES = 30;

const labelCls = "mb-2.5 block font-sans text-[13px] font-semibold text-[var(--text-tertiary)]";
const inputCls =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] px-3.5 py-2.5 font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow-border-strong)]";
const chipCls = (selected) =>
  cn(
    "max-w-full rounded-full border px-4 py-2 text-left font-sans text-[14px] transition-colors",
    selected
      ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] font-semibold text-[var(--accent-yellow-text)]"
      : "border-[var(--studio-divider)] bg-[var(--studio-raised)] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
  );

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {{item?: string, assignmentId?: string}|null} [props.preset] - What it opens on.
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(log: Object) => void} [props.onLogged] - After a log is saved.
 */
export function StudentLogPracticeDialog({ open, preset, onOpenChange, onLogged }) {
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const [choices, setChoices] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [other, setOther] = useState("");
  const [date, setDate] = useState(localDay);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // One save at a time: a second click before the first answers sends nothing.
  const savingRef = useRef(false);

  // Fresh each time it opens, on the item it was opened from.
  useEffect(() => {
    if (!open) return;
    setChosen(preset?.assignmentId ? `a:${preset.assignmentId}` : preset?.item ? `i:${preset.item}` : null);
    setMinutes(DEFAULT_MINUTES);
    setOther("");
    setDate(localDay());
    setNotes("");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // What it can be for: open assignments where those are on, else the latest
  // lesson's practice items.
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        if (ASSIGNMENTS_ENABLED) {
          const res = await studentApi.get("/student/assignments?status=open", { signal: controller.signal });
          const openOnes = res.data || [];
          if (openOnes.length) {
            setChoices(openOnes.map((a) => ({ key: `a:${a.assignment_id}`, label: a.text, assignmentId: a.assignment_id })));
            return;
          }
        }
        const res = await studentApi.get("/student/lessons?limit=20", { signal: controller.signal });
        const items = practiceItems(latestPracticeLesson(res.data || []));
        setChoices(items.map((item) => ({ key: `i:${item}`, label: item, item })));
      } catch (_err) {
        if (!controller.signal.aborted) setChoices([]);
      }
    })();
    return () => controller.abort();
  }, [open]);

  const shown = useMemo(() => {
    if (preset?.item && !choices.some((c) => c.item === preset.item)) {
      return [{ key: `i:${preset.item}`, label: preset.item, item: preset.item }, ...choices];
    }
    return choices;
  }, [choices, preset]);

  const usingOther = other.trim() !== "";
  const duration = usingOther ? Number(other) : minutes;
  const durationValid = Number.isInteger(duration) && duration >= 1 && duration <= MAX_MINUTES;

  const save = async (e) => {
    e?.preventDefault?.();
    if (savingRef.current) return;
    if (!durationValid) {
      setError(`Enter a length between 1 and ${MAX_MINUTES} minutes.`);
      return;
    }
    if (!date) {
      setError("Choose the day you practiced.");
      return;
    }
    if (date > localDay()) {
      setError("Choose a day that isn't in the future.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    const choice = shown.find((c) => c.key === chosen) || null;
    const payload = { duration_minutes: duration, notes: notes.trim() || null, date };
    if (choice?.item) payload.practice_item = choice.item;
    if (choice?.assignmentId) payload.assignment_id = choice.assignmentId;
    try {
      const log = await createPracticeLog(payload);
      onLogged?.(log);
      onOpenChange(false);
    } catch (_err) {
      setError("That practice wasn't logged. Try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const recordInstead = () => {
    onOpenChange(false);
    navigate("/student/rehearsals?record=1");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="studio max-h-[88vh] overflow-y-auto border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-[520px]"
        data-testid="practice-log-form"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-[24px] font-normal tracking-[-0.01em] text-[var(--text-primary)]">
            Log practice
          </DialogTitle>
          <DialogDescription className="sr-only">How long you practiced, and what it was for.</DialogDescription>
        </DialogHeader>

        <form onSubmit={save} noValidate>
          <div className="mb-5">
            <span id="practice-log-for-label" className={labelCls}>What it was for</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="practice-log-for-label">
              {shown.map((c, i) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={chosen === c.key}
                  onClick={() => setChosen(c.key)}
                  className={chipCls(chosen === c.key)}
                  data-testid={`practice-log-for-${i}`}
                >
                  {c.label}
                </button>
              ))}
              <button
                type="button"
                role="radio"
                aria-checked={chosen === null}
                onClick={() => setChosen(null)}
                className={chipCls(chosen === null)}
                data-testid="practice-log-for-else"
              >
                Something else
              </button>
            </div>
          </div>

          <div className="mb-5">
            <span id="practice-log-duration-label" className={labelCls}>How long</span>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-labelledby="practice-log-duration-label">
              {DURATIONS.map((d) => {
                const selected = !usingOther && minutes === d;
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => { setMinutes(d); setOther(""); setError(""); }}
                    className={chipCls(selected)}
                  >
                    {d} min
                  </button>
                );
              })}
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_MINUTES}
                step={1}
                value={other}
                onChange={(e) => { setOther(e.target.value); setError(""); }}
                placeholder="Other"
                aria-label="Other length in minutes"
                className={cn(inputCls, "w-[104px] rounded-full px-4 py-2 text-[14px]")}
              />
            </div>
          </div>

          <div className="mb-5 max-w-[220px]">
            <label htmlFor="practice-log-date" className={labelCls}>When</label>
            <input
              id="practice-log-date"
              type="date"
              value={date}
              max={localDay()}
              onChange={(e) => { setDate(e.target.value); setError(""); }}
              className={inputCls}
            />
          </div>

          <div className="mb-5">
            <label htmlFor="practice-log-notes" className={labelCls}>Notes (optional)</label>
            <textarea
              id="practice-log-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What went well, what was hard"
              maxLength={MAX_NOTES}
              rows={3}
              className={cn(inputCls, "min-h-[88px] resize-y")}
            />
          </div>

          {rehearsalsEnabled(student) && (
            <button
              type="button"
              onClick={recordInstead}
              className="mb-5 flex w-full items-center gap-3 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-3 text-left transition-colors hover:bg-[var(--studio-interactive)]"
              data-testid="practice-log-record"
            >
              <Icon name="mic" size={19} className="text-[var(--accent-yellow-text)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-sans text-[15px] font-semibold text-[var(--text-primary)]">Record instead</span>
                <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">Your teacher can listen and reply</span>
              </span>
              <Icon name="chevronRight" size={16} className="text-[var(--text-tertiary)]" />
            </button>
          )}

          {error && (
            <p role="alert" data-testid="practice-log-validation" className="mb-4 font-sans text-[13px] text-[var(--state-danger)]">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" icon="check" disabled={saving} data-testid="practice-log-submit">
              {saving ? "Logging…" : durationValid ? `Log ${duration} minutes` : "Log practice"}
            </Button>
            <Button type="button" variant="bare" onPress={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default StudentLogPracticeDialog;
